// Finance (Admins only): payment terms, the supplier payment schedule of a PO, agent commission, freight & logistics
// cost lines, statuses, the Incoterm check, cash-flow weeks and payables ageing. Pure functions shared by the server
// and the screens.

export const TRIGGERS = [
  ["po_confirmed", "PO confirmed"],
  ["before_shipment", "Before shipment (final inspection passed)"],
  ["copy_bl", "Copy B/L / shipping docs received"],
  ["etd", "ETD"],
  ["eta", "ETA"],
  ["invoice_date", "Invoice date"],
  ["fixed_date", "Fixed date"],
] as const;
export type Trigger = (typeof TRIGGERS)[number][0];
export const TRIGGER_LABEL = Object.fromEntries(TRIGGERS) as Record<Trigger, string>;
/** "Waiting for: …" wording. */
export const WAITING_LABEL: Record<Trigger, string> = {
  po_confirmed: "PO confirmed", before_shipment: "final inspection passed", copy_bl: "copy B/L", etd: "ETD", eta: "ETA", invoice_date: "invoice", fixed_date: "a date",
};
/** Instalments with these triggers are paid per shipment, on what the shipment carries. */
export const SHIPMENT_TRIGGERS: Trigger[] = ["before_shipment", "copy_bl", "etd", "eta", "invoice_date"];
/** Agents and logistics providers: one rule (e.g. 30 days after ETD, or after the invoice date). */
export const PAYEE_TRIGGERS: Trigger[] = ["etd", "eta", "copy_bl", "invoice_date"];

/** How an instalment is paid. Letter-of-credit instalments fall due on the L/C presentation's maturity date. */
export const PAYMENT_METHODS = [["tt", "T/T (bank transfer)"], ["lc", "Letter of credit"], ["dp", "D/P (documents against payment)"], ["da", "D/A (documents against acceptance)"], ["open", "Open account"], ["other", "Other"]] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number][0];
export const METHOD_SHORT: Record<string, string> = { tt: "T/T", lc: "L/C", dp: "D/P", da: "D/A", open: "Open account", other: "Other" };
export interface Instalment { name: string; pct: number; trigger: Trigger; days: number; fixedDate?: string; method?: PaymentMethod }
export interface TermsTemplate { id: string; name: string; instalments: Instalment[] }

export const DEFAULT_TERMS: TermsTemplate[] = [
  { id: "t-30-70-bl", name: "30% deposit / 70% balance against copy B/L", instalments: [
    { name: "Deposit", pct: 30, trigger: "po_confirmed", days: 7 }, { name: "Balance", pct: 70, trigger: "copy_bl", days: 0 }] },
  { id: "t-50-50", name: "50/50", instalments: [
    { name: "Deposit", pct: 50, trigger: "po_confirmed", days: 7 }, { name: "Balance", pct: 50, trigger: "copy_bl", days: 0 }] },
  { id: "t-100-before", name: "100% before shipment", instalments: [
    { name: "Payment", pct: 100, trigger: "before_shipment", days: 0 }] },
];

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
export const round2 = r2;
const isDate = (v: unknown) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ""));

export function normalizeInstalments(v: unknown): Instalment[] {
  const list = (Array.isArray(v) ? v : []).slice(0, 12).map((x: any) => ({
    name: String(x?.name || "").trim().slice(0, 60) || "Instalment",
    pct: Math.max(0, Math.min(100, Math.round(Number(x?.pct || 0) * 1000) / 1000)),
    trigger: (TRIGGERS.some(([k]) => k === x?.trigger) ? x.trigger : "po_confirmed") as Trigger,
    days: Math.max(-365, Math.min(365, Math.round(Number(x?.days) || 0))),
    method: (PAYMENT_METHODS.some(([k]) => k === x?.method) ? x.method : "tt") as PaymentMethod,
    ...(x?.trigger === "fixed_date" && isDate(x?.fixedDate) ? { fixedDate: String(x.fixedDate) } : {}),
  }));
  return list;
}
/** Problems with a set of instalments ("" when fine): they must add up to 100%. */
export function instalmentProblem(list: Instalment[]): string {
  if (!list.length) return "Add at least one instalment.";
  const total = Math.round(list.reduce((a, i) => a + i.pct, 0) * 1000) / 1000;
  if (Math.abs(total - 100) > 0.001) return `The instalments add up to ${total}%. They must add up to 100%.`;
  if (list.some((i) => i.pct <= 0)) return "Each instalment needs a percentage above 0.";
  return "";
}
export function normalizeTemplates(v: unknown): TermsTemplate[] {
  if (!Array.isArray(v)) return DEFAULT_TERMS;
  let n = 0;
  return v.slice(0, 50).map((t: any) => ({
    id: String(t?.id || `t-${Date.now().toString(36)}-${(n++).toString(36)}`).slice(0, 40),
    name: String(t?.name || "").trim().slice(0, 120) || "Terms",
    instalments: normalizeInstalments(t?.instalments),
  }));
}
/** "Copy B/L…" → "copy B/L…", but "PO confirmed" and "ETD" stay as they are. */
export const lowerFirst = (s: string) => (/^[A-Z][A-Z/]/.test(s) ? s : s.charAt(0).toLowerCase() + s.slice(1));
export const termsSummary = (list: Instalment[]) =>
  list.map((i) => `${i.pct}% ${lowerFirst(i.name)}${i.method && i.method !== "tt" ? ` by ${METHOD_SHORT[i.method]}` : ""} ${i.trigger === "fixed_date" ? `on ${i.fixedDate || "a fixed date"}` : `${i.days ? `${i.days} days after` : "on"} ${lowerFirst(TRIGGER_LABEL[i.trigger])}`}`).join(" · ");

export const TAX_TREATMENTS = [["border", "Paid at the border"], ["deferred", "Deferred to the tax return"], ["postponed", "Postponed accounting"]] as const;
export type TaxTreatment = (typeof TAX_TREATMENTS)[number][0];
/** Feature switches and rules (Settings → Finance). */
export interface FinanceRules {
  freightOn: boolean; customsOn: boolean; lcOn: boolean; whtOn: boolean;
  /** Advanced features (off unless switched on): freight quotes and the accounting export. */
  quotesOn: boolean; exportOn: boolean;
  invoiceTolerancePct: number;     // supplier / agent invoices vs Falcon's figure
  freightTolerancePct: number;     // forwarder invoice vs quote
  taxTreatment: TaxTreatment;      // import tax, changeable per shipment
  customsAuthority: string; valuation: "CIF" | "FOB";
  airDivisor: number; courierDivisor: number;
  conformity: Array<{ country: string; scheme: string }>;
}
export function normalizeFinanceSettings(v: any, defaults: { customsAuthority?: string; valuation?: "CIF" | "FOB"; conformity?: Array<{ country: string; scheme: string }> } = {}): FinanceRules {
  const pct = (x: unknown, d: number) => (x !== null && x !== undefined && x !== "" && Number(x) >= 0 && Number(x) <= 100 ? Math.round(Number(x) * 100) / 100 : d);
  const div = (x: unknown, d: number) => (Number(x) >= 1000 && Number(x) <= 10000 ? Math.round(Number(x)) : d);
  return {
    freightOn: !!v?.freightOn, customsOn: v?.customsOn === undefined ? true : !!v.customsOn, lcOn: !!v?.lcOn, whtOn: !!v?.whtOn, quotesOn: !!v?.quotesOn, exportOn: !!v?.exportOn,
    invoiceTolerancePct: pct(v?.invoiceTolerancePct, 0.5), freightTolerancePct: pct(v?.freightTolerancePct, 5),
    taxTreatment: (TAX_TREATMENTS.some(([k]) => k === v?.taxTreatment) ? v.taxTreatment : "border") as TaxTreatment,
    customsAuthority: String(v?.customsAuthority ?? defaults.customsAuthority ?? "Customs").trim().slice(0, 120) || "Customs",
    valuation: v?.valuation === "FOB" || v?.valuation === "CIF" ? v.valuation : defaults.valuation || "CIF",
    airDivisor: div(v?.airDivisor, 6000), courierDivisor: div(v?.courierDivisor, 5000),
    conformity: Array.isArray(v?.conformity) ? v.conformity.slice(0, 100).map((c: any) => ({ country: String(c?.country || "").toUpperCase().slice(0, 2), scheme: String(c?.scheme || "").trim().slice(0, 120) })).filter((c: any) => c.country && c.scheme) : defaults.conformity || [],
  };
}

/** The organisation's finance profile (Settings → Finance → Organisation profile). */
export interface FinanceProfile {
  country: string; localCurrency: string; tradingCurrency: string; timezone: string; fiscalYearStart: number; workingDays: number[];
  moveToWorkingDay: boolean; dateFormat: "dd/mm/yyyy" | "mm/dd/yyyy" | "yyyy-mm-dd"; numberFormat: "1,234.56" | "1.234,56" | "1 234,56"; hijri: boolean;
}
export interface Holiday { date: string; name: string }
export interface TaxRate { id: string; name: string; rate: number; recoverable: boolean }
export function normalizeTaxes(v: unknown): TaxRate[] {
  let n = 0;
  return (Array.isArray(v) ? v : []).slice(0, 40).map((t: any) => ({
    id: String(t?.id || `tax-${Date.now().toString(36)}-${(n++).toString(36)}`).slice(0, 40), name: String(t?.name || "").trim().slice(0, 60) || "Tax",
    rate: Math.max(0, Math.min(100, Math.round(Number(t?.rate || 0) * 1000) / 1000)), recoverable: t?.recoverable === undefined ? true : !!t.recoverable,
  }));
}
export function normalizeHolidays(v: unknown): Holiday[] {
  const out = (Array.isArray(v) ? v : []).map((h: any) => ({ date: String(h?.date || ""), name: String(h?.name || "").trim().slice(0, 120) })).filter((h) => isDate(h.date));
  return [...new Map(out.map((h) => [h.date, h])).values()].sort((a, b) => a.date.localeCompare(b.date)).slice(0, 1000);
}
/** Holidays from a CSV: date,name (dd/mm/yyyy or yyyy-mm-dd). */
export function parseHolidaysCsv(text: string): { rows: Holiday[]; errors: string[] } {
  const rows: Holiday[] = [], errors: string[] = [];
  text.replace(/^\uFEFF/, "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean).forEach((line, i) => {
    const [d, ...rest] = line.split(/[,;\t]/).map((c) => c.trim().replace(/^"|"$/g, ""));
    if (i === 0 && /date/i.test(d)) return;
    const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(d);
    const iso = isDate(d) ? d : m ? `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}` : "";
    if (!iso) { errors.push(`Line ${i + 1}: "${line}" — expected date,name`); return; }
    rows.push({ date: iso, name: rest.join(" ").trim() || "Holiday" });
  });
  return { rows, errors };
}
/** The next working day on or after a date (weekend days and holidays skipped). */
export function nextWorkingDay(date: string, workingDays: number[], holidays: Set<string>): string {
  if (!workingDays.length) return date;
  let d = date;
  for (let i = 0; i < 60; i++) {
    const wd = new Date(`${d}T12:00:00Z`).getUTCDay();
    if (workingDays.includes(wd) && !holidays.has(d)) return d;
    d = addDays(d, 1);
  }
  return date;
}

/** Accounting export: account codes per cost type and payee type, tax codes per tax. */
export const ACCOUNT_KEYS = [
  ["goods", "Goods purchased (supplier invoices / payments)"], ["commission", "Agent commission"], ["freight", "Freight"], ["origin", "Origin charges"], ["destination", "Destination charges"],
  ["clearance", "Customs clearance fee"], ["haulage", "Haulage / delivery"], ["insurance", "Insurance"], ["storage", "Storage / demurrage / detention"], ["other", "Other logistics costs"],
  ["duty", "Import duty"], ["importTax", "Import tax (recoverable)"], ["customsOther", "Other import charges"], ["bankCharges", "Bank charges"], ["fx", "Exchange gain / loss"], ["wht", "Withholding tax payable"],
  ["apSupplier", "Accounts payable – suppliers"], ["apAgent", "Accounts payable – agents"], ["apLogistics", "Accounts payable – logistics"], ["apCustoms", "Accounts payable – customs"], ["bank", "Bank"],
] as const;
export type AccountKey = (typeof ACCOUNT_KEYS)[number][0];
export interface AccountMapping { accounts: Partial<Record<AccountKey, string>>; taxCodes: Record<string, string> }
export function normalizeAccounts(v: any): AccountMapping {
  const accounts: Partial<Record<AccountKey, string>> = {};
  for (const [k] of ACCOUNT_KEYS) { const x = String(v?.accounts?.[k] ?? "").trim().slice(0, 40); if (x) accounts[k] = x; }
  const taxCodes: Record<string, string> = {};
  for (const [k, x] of Object.entries(v?.taxCodes || {})) { const c = String(x ?? "").trim().slice(0, 40); if (c) taxCodes[String(k).slice(0, 40)] = c; }
  return { accounts, taxCodes };
}

// ---- Supplier payment schedule ---------------------------------------------------------------------------------------
export interface ScheduleLine { id: string; qty: number; price: number }
export interface ScheduleShipment {
  id: string; label: string;
  qty: Record<string, number>;       // line id → shipped quantity
  closed: Record<string, number>;    // line id → factory shortfall closed on this shipment
  dates: Partial<Record<Trigger, string | null>>;
}
export interface Existing { key: string; paid: number; frozen: number | null }
export interface Planned {
  key: string; seq: number; name: string; pct: number; trigger: Trigger; days: number; method: PaymentMethod;
  shipmentId: string | null; shipmentLabel: string; amount: number; triggerDate: string | null; dueDate: string | null; waitingFor: string;
}
export const addDays = (d: string, days: number) => {
  const t = new Date(`${d}T12:00:00Z`);
  t.setUTCDate(t.getUTCDate() + days);
  return t.toISOString().slice(0, 10);
};

/**
 * The PO's instalments. Instalments on PO confirmed / a fixed date are for the whole order (their amount stays once
 * fully paid). The others are paid per shipment on what it carries: shipped value − that shipment's share of the
 * order-level instalments; whatever is not shipped yet waits as "Not yet shipped". Paid amounts never change: an
 * overpaid or fully paid part stays as it is and the difference moves to the other unpaid parts.
 */
export function supplierSchedule(input: {
  instalments: Instalment[]; lines: ScheduleLine[]; shipments: ScheduleShipment[];
  orderDates: Partial<Record<Trigger, string | null>>;      // po_confirmed, and dates for the not-yet-shipped part
  existing: Existing[];
  /** Moves a due date to a working day (weekends / holidays), when the organisation asks for it. */
  adjust?: (date: string) => string;
}): Planned[] {
  const ex = new Map(input.existing.map((e) => [e.key, e]));
  const orderValue = r2(input.lines.reduce((a, l) => a + l.qty * l.price, 0));
  const price = new Map(input.lines.map((l) => [l.id, l.price]));
  const out: Planned[] = [];
  const due = (trigger: Trigger, days: number, date: string | null | undefined, fixed?: string) => {
    const t = trigger === "fixed_date" ? (fixed || null) : (date || null);
    const raw = t ? addDays(t, trigger === "fixed_date" ? 0 : days) : null;
    return { triggerDate: t, dueDate: raw && input.adjust ? input.adjust(raw) : raw };
  };
  // Order-level instalments.
  let deposits = 0;
  input.instalments.forEach((i, idx) => {
    if (SHIPMENT_TRIGGERS.includes(i.trigger)) return;
    const key = String(idx);
    const e = ex.get(key);
    const amount = e?.frozen ?? r2((orderValue * i.pct) / 100);
    deposits += amount;
    const d = due(i.trigger, i.days, input.orderDates[i.trigger], i.fixedDate);
    out.push({ key, seq: idx * 1000, name: i.name, pct: i.pct, trigger: i.trigger, days: i.days, method: i.method || "tt", shipmentId: null, shipmentLabel: "", amount, ...d,
      waitingFor: d.dueDate ? "" : WAITING_LABEL[i.trigger] });
  });
  // Shipped and still-open values.
  const shipped = input.shipments.map((s) => ({ s, value: r2(Object.entries(s.qty).reduce((a, [id, q]) => a + q * (price.get(id) || 0), 0)) }));
  let rest = 0;
  for (const l of input.lines) {
    const sent = input.shipments.reduce((a, s) => a + (s.qty[l.id] || 0) + (s.closed[l.id] || 0), 0);
    rest += Math.max(0, l.qty - sent) * l.price;
  }
  rest = r2(rest);
  const total = shipped.reduce((a, x) => a + x.value, 0) + rest;
  const balanceOf = (v: number) => Math.max(0, v - (total > 0 ? (deposits * v) / total : 0));
  const perShip = input.instalments.map((i, idx) => ({ i, idx })).filter((x) => SHIPMENT_TRIGGERS.includes(x.i.trigger));
  const shipPct = perShip.reduce((a, x) => a + x.i.pct, 0);
  for (const { i, idx } of perShip) {
    const share = shipPct > 0 ? i.pct / shipPct : 0;
    const targets: Array<{ key: string; computed: number; e?: Existing; shipmentId: string | null; label: string; dates: Partial<Record<Trigger, string | null>>; seq: number }> = [];
    shipped.forEach(({ s, value }, n) => {
      if (value <= 0 && !ex.has(`${idx}:${s.id}`)) return;
      targets.push({ key: `${idx}:${s.id}`, computed: r2(balanceOf(value) * share), e: ex.get(`${idx}:${s.id}`), shipmentId: s.id, label: s.label, dates: s.dates, seq: idx * 1000 + 1 + n });
    });
    if (rest > 0.004 || ex.has(`${idx}:rest`)) {
      targets.push({ key: `${idx}:rest`, computed: r2(balanceOf(rest) * share), e: ex.get(`${idx}:rest`), shipmentId: null, label: "Not yet shipped", dates: input.orderDates, seq: idx * 1000 + 999 });
    }
    // Paid amounts never change: fully paid (frozen) or overpaid parts stay; the difference goes to the unpaid ones.
    const amt = targets.map((t) => (t.e?.frozen ?? Math.max(t.computed, t.e?.paid || 0)));
    let excess = targets.reduce((a, t, k) => a + amt[k] - t.computed, 0);
    const adjustable = targets.map((t, k) => ({ t, k })).filter(({ t }) => t.e?.frozen == null).reverse();   // not-yet-shipped first, then newest shipments
    for (const { t, k } of adjustable) {
      if (Math.abs(excess) < 0.005) break;
      if (excess > 0) {
        const room = amt[k] - (t.e?.paid || 0);
        const take = Math.min(room, excess);
        amt[k] = r2(amt[k] - take); excess = r2(excess - take);
      } else { amt[k] = r2(amt[k] - excess); excess = 0; }
    }
    if (excess < -0.005) {
      targets.push({ key: `${idx}:adj`, computed: r2(-excess), e: ex.get(`${idx}:adj`), shipmentId: null, label: "Adjustment", dates: input.orderDates, seq: idx * 1000 + 998 });
      amt.push(r2(-excess));
    }
    targets.forEach((t, k) => {
      const d = due(i.trigger, i.days, t.dates[i.trigger]);
      out.push({ key: t.key, seq: t.seq, name: i.name, pct: i.pct, trigger: i.trigger, days: i.days, method: i.method || "tt", shipmentId: t.shipmentId, shipmentLabel: t.label, amount: r2(amt[k]), ...d,
        waitingFor: d.dueDate ? "" : WAITING_LABEL[i.trigger] });
    });
  }
  return out.sort((a, b) => a.seq - b.seq);
}

/** Over / under-shipment of a PO: shipped (and closed shortfall) against ordered. */
export function shipmentVariance(lines: ScheduleLine[], shipments: ScheduleShipment[]) {
  const ordered = lines.reduce((a, l) => a + l.qty, 0);
  const shipped = shipments.reduce((a, s) => a + Object.values(s.qty).reduce((x, q) => x + q, 0), 0);
  const closed = shipments.reduce((a, s) => a + Object.values(s.closed).reduce((x, q) => x + q, 0), 0);
  const diff = Math.round((shipped - ordered) * 100) / 100;
  return { ordered, shipped, closed, diff, pct: ordered ? Math.round((diff / ordered) * 10000) / 100 : 0, done: shipped + closed >= ordered - 0.0001 };
}

// ---- Status -----------------------------------------------------------------------------------------------------------
export type ObligationStatus = "Not due" | "Due" | "Overdue" | "Part-paid" | "Paid" | "Reversed";
export const DUE_SOON_DAYS = 7;
/** Not due / Due (within a week) / Overdue by date; Part-paid and Paid by what has been paid; Reversed when the only
 *  payments made were reversed and nothing is due yet. */
export function obligationStatus(o: { amount: number; paid: number; dueDate: string | null; reversed?: number }, today: string): ObligationStatus {
  if (o.amount > 0 && o.paid >= o.amount - 0.005) return "Paid";
  if (o.amount <= 0.004 && o.paid > 0) return "Paid";
  if (o.paid > 0.004) return "Part-paid";
  const dueState: ObligationStatus = !o.dueDate ? "Not due" : o.dueDate < today ? "Overdue" : o.dueDate <= addDays(today, DUE_SOON_DAYS) ? "Due" : "Not due";
  if ((o.reversed || 0) > 0 && dueState === "Not due") return "Reversed";
  return dueState;
}
/**
 * What is still to be paid on a PO — the ONE calculation every screen uses (PO Finance, Make payment, statements,
 * Ledgers, Home alerts, reports, accounting export): the PO's value less what has been paid less the agreed claims set to
 * "Deduct from next payment on this PO". `payable` also leaves out payments awaiting proof. Never below zero.
 */
export function poOutstanding(x: { value: number; paid: number; deducted: number; pending?: number }): { outstanding: number; payable: number } {
  const outstanding = r2(Math.max(0, x.value - x.paid - x.deducted));
  return { outstanding, payable: r2(Math.max(0, outstanding - (x.pending || 0))) };
}
/**
 * A PO's claim deductions shared over its supplier instalments that are still open: the deposit first, then the
 * shipment balances, then the rest — the "next payment" takes the deduction. Returns how much each instalment is lowered.
 */
export function shareDeduction(instalments: Array<{ id: string; open: number; order: number }>, deducted: number): Map<string, number> {
  const out = new Map<string, number>();
  let left = r2(deducted);
  for (const o of [...instalments].sort((a, b) => a.order - b.order)) {
    if (left <= 0.004) break;
    const take = r2(Math.min(Math.max(0, o.open), left));
    if (take > 0.004) { out.set(o.id, take); left = r2(left - take); }
  }
  return out;
}
export const STATUS_TONE: Record<ObligationStatus, string> = { "Not due": "gray", Due: "amber", Overdue: "red", "Part-paid": "amber", Paid: "green", Reversed: "gray" };
export const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86400000);

/** Payables ageing bucket for what is still outstanding. */
export const AGEING_BUCKETS = ["Not yet due", "1–30", "31–60", "61–90", "90+"] as const;
export function ageingBucket(dueDate: string | null, today: string): (typeof AGEING_BUCKETS)[number] {
  if (!dueDate || dueDate >= today) return "Not yet due";
  const d = daysBetween(dueDate, today);
  return d <= 30 ? "1–30" : d <= 60 ? "31–60" : d <= 90 ? "61–90" : "90+";
}
/** The Monday of the week a date is in. */
export function weekStart(d: string): string {
  const t = new Date(`${d}T12:00:00Z`);
  const wd = (t.getUTCDay() + 6) % 7;
  return addDays(d, -wd);
}

/** Invoice vs Falcon's figure: the difference and whether it is over the tolerance. */
export function invoiceCheck(invoiced: number, expected: number, tolerancePct: number) {
  const diff = r2(invoiced - expected);
  const pct = expected ? Math.round((diff / expected) * 10000) / 100 : invoiced ? 100 : 0;
  return { diff, pct, warn: Math.abs(pct) > tolerancePct + 1e-9 && Math.abs(diff) >= 0.01 };
}

// ---- Freight & logistics ------------------------------------------------------------------------------------------------
export const PROVIDER_KINDS = [["forwarder", "Freight forwarder"], ["broker", "Customs broker"], ["haulier", "Haulier / courier"], ["other", "Other logistics provider"]] as const;
export const PROVIDER_KIND_LABEL = Object.fromEntries(PROVIDER_KINDS) as Record<string, string>;
export const FREIGHT_MODES = ["Sea FCL", "Sea LCL", "Air", "Road", "Courier"] as const;
export const INCOTERMS = ["EXW", "FCA", "FAS", "FOB", "CFR", "CIF", "CPT", "CIP", "DAP", "DPU", "DDP"] as const;
/** Incoterms where the supplier pays the main freight. */
export const SELLER_PAYS_FREIGHT = ["CFR", "CIF", "CPT", "CIP", "DAP", "DPU", "DDP"];

/** Freight & logistics cost lines (duty and import tax are in Customs & duty). */
export const COST_CATEGORIES = [
  ["freight", "Freight"], ["origin", "Origin charges"], ["destination", "Destination charges (THC, documentation)"], ["clearance", "Customs clearance fee"],
  ["haulage", "Haulage / delivery"], ["insurance", "Insurance"], ["storage", "Storage / demurrage / detention"], ["other", "Other"],
] as const;
/** "duty" and "vat" lines on logistics invoices entered before Customs & duty had its own section. */
export type CostCategory = (typeof COST_CATEGORIES)[number][0] | "duty" | "vat";
export const COST_LABEL = { ...Object.fromEntries(COST_CATEGORIES), duty: "Import duty", vat: "Import VAT (recoverable)" } as Record<CostCategory, string>;
export const isCostCategory = (v: unknown): v is CostCategory => COST_CATEGORIES.some(([k]) => k === v) || v === "duty" || v === "vat";
export const isFreightCategory = (v: unknown) => COST_CATEGORIES.some(([k]) => k === v);
/** Import VAT is recoverable, so it is shown separately and never goes into the landed cost. */
export const LANDED_CATEGORIES: CostCategory[] = ["freight", "origin", "destination", "clearance", "duty", "haulage", "insurance", "storage", "other"];

/** How each shipment cost is spread over its PO lines (default per cost type, editable per shipment). */
export const ALLOC_METHODS = [["cbm", "CBM"], ["chargeable", "Chargeable weight"], ["value", "Value"], ["quantity", "Quantity"]] as const;
export type AllocMethod = (typeof ALLOC_METHODS)[number][0];
export function defaultAllocation(category: string, mode: string): AllocMethod {
  if (category === "freight") return mode === "Air" || mode === "Courier" ? "chargeable" : "cbm";
  if (["origin", "destination", "haulage", "storage"].includes(category)) return "cbm";
  return "value";
}

/** Warnings when charges don't fit the Incoterm (the supplier pays freight on C- and D-terms; duty on DDP). */
export function incotermWarnings(incoterm: string, amounts: Partial<Record<CostCategory, number>>): string[] {
  const out: string[] = [];
  const t = String(incoterm || "").toUpperCase();
  if (SELLER_PAYS_FREIGHT.includes(t) && (amounts.freight || 0) > 0) out.push(`Freight is recorded, but under ${t} the supplier pays the freight. Check the forwarder's invoice.`);
  if (t === "DDP" && ((amounts.duty || 0) > 0 || (amounts.vat || 0) > 0)) out.push("Import duty or VAT is recorded, but under DDP the supplier pays duty and taxes.");
  return out;
}

/** Spread an amount over lines by value, quantity or CBM (CBM falls back to quantity when unknown). */
export function spread(total: number, rows: Array<{ key: string; value: number; qty: number; cbm: number }>, method: "value" | "quantity" | "cbm"): Map<string, number> {
  const out = new Map<string, number>();
  let basis: "value" | "qty" | "cbm" = method === "quantity" ? "qty" : method === "cbm" ? "cbm" : "value";
  let sum = rows.reduce((a, r) => a + r[basis], 0);
  if (sum <= 0) { basis = "qty"; sum = rows.reduce((a, r) => a + r.qty, 0); }
  for (const r of rows) out.set(r.key, sum > 0 ? (total * r[basis]) / sum : 0);
  return out;
}

// ---- Bank details ------------------------------------------------------------------------------------------------------
export interface BankDetails {
  beneficiary: string; beneficiaryAddress: string; bank: string; bankAddress: string; account: string; swift: string;
  routingType: string; routingValue: string; intermediaryBank: string; intermediarySwift: string;
}
export const BANK_FIELDS: Array<[keyof BankDetails, string]> = [
  ["beneficiary", "Beneficiary name"], ["beneficiaryAddress", "Beneficiary address"], ["bank", "Bank name"], ["bankAddress", "Bank address"], ["account", "IBAN or account number"],
  ["swift", "SWIFT / BIC"], ["routingType", "Local routing code type (e.g. ABA, sort code, IFSC)"], ["routingValue", "Local routing code"],
  ["intermediaryBank", "Intermediary / correspondent bank (optional)"], ["intermediarySwift", "Intermediary SWIFT / BIC (optional)"],
];
export function normalizeBank(v: any): BankDetails {
  const s = (x: unknown, n = 200) => String(x ?? "").trim().slice(0, n);
  // Details saved before routing codes had a type kept a "sort code / routing" field.
  const routingValue = s(v?.routingValue ?? v?.sortCode, 40), routingType = s(v?.routingType, 40) || (v?.sortCode ? "Sort code / routing" : "");
  return {
    beneficiary: s(v?.beneficiary), beneficiaryAddress: s(v?.beneficiaryAddress, 400), bank: s(v?.bank), bankAddress: s(v?.bankAddress, 400), account: s(v?.account, 60).toUpperCase(),
    swift: s(v?.swift, 20).toUpperCase(), routingType, routingValue, intermediaryBank: s(v?.intermediaryBank), intermediarySwift: s(v?.intermediarySwift, 20).toUpperCase(),
  };
}
export const bankEmpty = (b: BankDetails) => !Object.values(b).some((x) => String(x || "").trim());
/** Account numbers are shown with only the last 4 characters. */
export const maskAccount = (v: string) => (v.length > 4 ? `${"•".repeat(Math.min(8, v.length - 4))}${v.slice(-4)}` : v);
/** How long the "bank details changed" warning stays unless an Admin verifies it by phone. */
export const BANK_WARNING_DAYS = 30;

// ---- Formats (organisation profile) --------------------------------------------------------------------------------------
export type DateFormat = FinanceProfile["dateFormat"];
/** A yyyy-mm-dd date in the organisation's date format. */
export function formatDateAs(iso: string | null | undefined, fmt: DateFormat): string {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!d) return String(iso);
  return fmt === "mm/dd/yyyy" ? `${m}/${d}/${y}` : fmt === "yyyy-mm-dd" ? `${y}-${m}-${d}` : `${d}/${m}/${y}`;
}
/** The Hijri (Umm al-Qura) date, shown alongside when the organisation turns it on. */
export function hijriDate(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("en-u-ca-islamic-umalqura", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${String(iso).slice(0, 10)}T00:00:00Z`));
  } catch { return ""; }
}
/** A number in the organisation's number format (for exports as text). */
export function formatNumberAs(n: number | null | undefined, fmt: FinanceProfile["numberFormat"], dp = 2): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  const s = Math.abs(n).toFixed(dp);
  const [i, f] = s.split(".");
  const [th, dec] = fmt === "1.234,56" ? [".", ","] : fmt === "1 234,56" ? [" ", ","] : [",", "."];
  return `${n < 0 ? "-" : ""}${i.replace(/\B(?=(\d{3})+(?!\d))/g, th)}${f ? dec + f : ""}`;
}
export const DISPLAY_MODES = ["trading", "local", "both"] as const;
export type DisplayMode = (typeof DISPLAY_MODES)[number];
